import React, { useEffect } from 'react';
import { SITE } from '../lib/site';

interface BreadcrumbItem {
  name: string;
  url: string;
}

interface SeoProps {
  title?: string;
  description?: string;
  image?: string;
  url?: string;
  type?: 'website' | 'article' | 'product';
  noindex?: boolean;
  breadcrumbs?: BreadcrumbItem[];
  serviceSchema?: {
    name: string;
    description: string;
    providerName?: string;
    areaServed?: string[];
  };
  faqSchema?: { q: string; a: string }[];
  extraJsonLd?: object[];
  publishedTime?: string;
  modifiedTime?: string;
}

/**
 * SEO dinámico por página (SPA): title, meta, OG/Twitter, canonical,
 * JSON-LD (Service, FAQPage, BreadcrumbList + extra). El JSON-LD estático
 * de LocalBusiness vive en public/index.html — aquí no se duplica.
 */
const SEO = ({
  title,
  description,
  image,
  url,
  type = 'website',
  noindex = false,
  breadcrumbs,
  serviceSchema,
  faqSchema,
  extraJsonLd = [],
  publishedTime,
  modifiedTime,
}: SeoProps) => {
  useEffect(() => {
    const siteTitle = title
      ? `${title} | ${SITE.name}`
      : `${SITE.name} | Construcción, Reformas y Mantenimiento en Málaga`;

    const siteDescription =
      description ??
      'Empresa de construcción, reformas integrales y mantenimiento en Málaga. +15 años de experiencia, +150 proyectos entregados y presupuesto gratis en 24h.';

    const siteImage = image
      ? image.startsWith('http') ? image : `${SITE.domain}${image}`
      : `${SITE.domain}/fotos-webp/5678.webp`;

    const siteUrl = url ? (url.startsWith('http') ? url : `${SITE.domain}${url}`) : `${SITE.domain}/`;

    document.title = siteTitle;

    const setMeta = (attr: 'name' | 'property', key: string, content: string) => {
      let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };
    const removeMeta = (attr: 'name' | 'property', key: string) => {
      document.head.querySelector(`meta[${attr}="${key}"]`)?.remove();
    };

    // Básicos
    setMeta('name', 'description', siteDescription);
    setMeta('name', 'robots', noindex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large, max-snippet:-1');
    setMeta('name', 'author', SITE.name);

    // OG
    setMeta('property', 'og:title', siteTitle);
    setMeta('property', 'og:description', siteDescription);
    setMeta('property', 'og:url', siteUrl);
    setMeta('property', 'og:type', type);
    setMeta('property', 'og:image', siteImage);
    setMeta('property', 'og:site_name', SITE.name);

    // Twitter
    setMeta('name', 'twitter:card', 'summary_large_image');
    setMeta('name', 'twitter:title', siteTitle);
    setMeta('name', 'twitter:description', siteDescription);
    setMeta('name', 'twitter:image', siteImage);

    // Article extra
    if (type === 'article' && publishedTime) {
      setMeta('property', 'article:published_time', publishedTime);
      setMeta('property', 'article:modified_time', modifiedTime ?? publishedTime);
    } else {
      removeMeta('property', 'article:published_time');
      removeMeta('property', 'article:modified_time');
    }

    // Canonical
    let canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = siteUrl;

    // JSON-LD dinámico
    const blocks: object[] = [];

    if (breadcrumbs && breadcrumbs.length > 1) {
      blocks.push({
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: breadcrumbs.map((b, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          name: b.name,
          item: b.url.startsWith('http') ? b.url : `${SITE.domain}${b.url}`,
        })),
      });
    }

    if (serviceSchema) {
      blocks.push({
        '@context': 'https://schema.org',
        '@type': 'Service',
        name: serviceSchema.name,
        description: serviceSchema.description,
        provider: {
          '@type': 'ConstructionCompany',
          name: SITE.name,
          telephone: SITE.phone,
          url: SITE.domain,
        },
        areaServed: (serviceSchema.areaServed ?? ['Málaga']).map((c) => ({ '@type': 'City', name: c })),
        hasOfferCatalog: {
          '@type': 'OfferCatalog',
          name: serviceSchema.name,
        },
      });
    }

    if (faqSchema?.length) {
      blocks.push({
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqSchema.map((f) => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a },
        })),
      });
    }

    blocks.push(...extraJsonLd);

    const container = document.querySelector('#seo-jsonld');
    if (container) container.remove();
    if (blocks.length) {
      const holder = document.createElement('script');
      holder.id = 'seo-jsonld';
      holder.type = 'application/ld+json';
      holder.textContent = JSON.stringify(blocks);
      document.head.appendChild(holder);
    }
  }, [title, description, image, url, type, noindex, breadcrumbs, serviceSchema, faqSchema, extraJsonLd, publishedTime, modifiedTime]);

  return null;
};

export default SEO;
